/**
 * Persistence hooks for auto-save functionality
 * Handles debounced sang and storage integration
 *
 * Session-scope store state:
 * - `combatStates` (createCombatStatesSlice) — in-memory only, resets on reload
 * - `combatRound` (createCombatRoundSlice) — in-memory only, resets on reload
 *   (spec: .kiro/specs/active-effects-round-tracker, leaf 1.6)
 *   The active-effects tracker is intentionally session-scoped to match the
 *   sibling combatStates slice; persisting it would require a LocalStorage
 *   schema migration that falls outside the spec's no-touch boundary.
 *   Backward-compatible: saves produced before this feature reload cleanly;
 *   the slice always initialises to { isInCombat: false, roundNumber: 0, ... }.
 */

import { useEffect, useCallback, useRef } from 'react';
import { useCharacterStore } from './character';
import type { Character } from '../types/character';
import { localStorageService } from '../services/LocalStorageService';
import { getActiveCharacterId } from '../utils/cookieUtils';
import { localCharacterService } from '../services/LocalCharacterService';

// Debounce delay for auto-save (500ms as per steering rules)
const AUTO_SAVE_DELAY = 500;

/**
 * Hook for auto-sang character data to localStorage
 * Debounces saves to avoid excessive writes
 */
export function useAutoSave() {
  const character = useCharacterStore((state) => state.character);
  const hasUnsavedChanges = useCharacterStore((state) => state.hasUnsavedChanges);
  const setSaved = useCharacterStore((state) => state._setSaved);
  const setLoading = useCharacterStore((state) => state._setLoading);

  const timeout = useRef<NodeJS.Timeout>();
  const lastSave = useRef<string>('');

  const saveToLocalStorage = useCallback(
    async (characterData: Character) => {
      try {
        import('../utils/logger').then(({ storeLogger }) => {
          storeLogger.info('Starting save operation', { characterId: characterData.id });
        });
        setLoading(true);

        // Use LocalStorageSerce for sang
        const result = await localStorageService.save(characterData);

        if (result.success) {
          // Update store state
          setSaved();
          lastSave.current = JSON.stringify(characterData);
          console.log('✅ Persistence:auto-saved to localStorage');
        } else {
          console.error('❌ Persistence: Failed to save character:', result.error);
          // TODO: Show user notification about save failure
        }
      } catch (error) {
        console.error('❌ Persistence: Failed to save character to localStorage:', error);
        // TODO: Show user notification about save failure
      } finally {
        setLoading(false);
        console.log('🏁 Persistence: Save operation completed');
      }
    },
    [setLoading, setSaved]
  );

  // Debounced auto-save effect
  useEffect(() => {
    console.log(
      '🔄 Persistence: Auto-save check - character:',
      !!character,
      'hasUnsavedChanges:',
      hasUnsavedChanges
    );

    if (!character || !hasUnsavedChanges) {
      return;
    }

    // Check if character data has actually changed
    const currentData = JSON.stringify(character);

    if (currentData === lastSave.current) {
      console.log('📋 Persistence:data unchanged, skipping save');
      return;
    }

    console.log('⏰ Persistence: Scheduling auto-save in', AUTO_SAVE_DELAY, 'ms');

    // Clear existing timeout
    if (timeout.current) {
      clearTimeout(timeout.current);
    }

    // Set new timeout for debounced save
    timeout.current = setTimeout(() => {
      saveToLocalStorage(character);
    }, AUTO_SAVE_DELAY);

    // Cleanup timeout on unmount or dependency change
    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    };
  }, [character, hasUnsavedChanges, saveToLocalStorage]);

  // Save immediately when nagating away
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (character && hasUnsavedChanges) {
        // Clear timeout and save immediately
        if (timeout.current) {
          clearTimeout(timeout.current);
        }

        // Use synchronous save for beforeunload (best effort)
        try {
          localStorageService.save(character);
        } catch (error) {
          console.error('Failed to save character on page unload:', error);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [character, hasUnsavedChanges]);

  return {
    saveNow: () => {
      if (character) {
        if (timeout.current) {
          clearTimeout(timeout.current);
        }
        saveToLocalStorage(character);
      }
    }
  };
}

/**
 * Hook for loading character data from localStorage
 */
export function useLoad() {
  const load = useCharacterStore((state) => state.load);
  const setLoading = useCharacterStore((state) => state._setLoading);
  const setValidationErrors = useCharacterStore((state) => state._setValidationErrors);

  const loadFromLocalStorage = useCallback(
    async (characterId?: string): Promise<Character | null> => {
      try {
        setLoading(true);

        // Use LocalStorageSerce for loading
        const result = await localStorageService.load(characterId);

        if (result.success && result.character) {
          load(result.character);
          console.log('loaded from localStorage');
          return result.character;
        } else {
          console.error('Failed to load character:', result.error);
          setValidationErrors([result.error || 'Failed to load character data']);
          return null;
        }
      } catch (error) {
        console.error('Failed to load character from localStorage:', error);
        setValidationErrors(['Failed to load character data from storage']);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [load, setLoading, setValidationErrors]
  );

  return {
    loadFromLocalStorage,
    hasStored: () => {
      // Check if any character data exists in storage
      return localStorageService.getStorageInfo().used > 0;
    },
    clearStored: async () => {
      await localStorageService.clearAllData();
    }
  };
}

/**
 * Hook for managing character persistence state
 */
export function usePersistenceState() {
  const isLoading = useCharacterStore((state) => state.isLoading);
  const hasUnsavedChanges = useCharacterStore((state) => state.hasUnsavedChanges);
  const lastSaved = useCharacterStore((state) => state.lastSaved);
  const validationErrors = useCharacterStore((state) => state.validationErrors);

  return {
    isLoading,
    hasUnsavedChanges,
    lastSaved,
    validationErrors,
    hasPersistenceErrors: validationErrors.length > 0
  };
}

/**
 * Hook for Google Drive sync with offline queue support
 */
// TEMPORARILY DISABLED - Google Drive sync causing crashes
// export function useGoogleDriveSync() {
//   return {
//     saveToGoogleDrive: async () => {},
//     syncStatus: { isOnline: true, pendingOperations: 0, lastSync: null }
//   };
// }

/**
 * Hook that combines auto-save and load functionality
 * Use this in your main App component
 */
export function usePersistence() {
  console.log('🚀 Persistence: usePersistence hook initialized');

  // Initialize auto-save functionality
  const { saveNow } = useAutoSave();

  // Initialize load functionality
  const { loadFromLocalStorage, hasStored, clearStored } = useLoad();

  // Get persistence state
  const { isLoading, hasUnsavedChanges, lastSaved, validationErrors, hasPersistenceErrors } =
    usePersistenceState();

  // Get load function from character store
  const load = useCharacterStore((state) => state.load);

  // Auto-load character on first mount
  useEffect(() => {
    console.log('🔍 Persistence: Checking for stored character...');

    // First check for character ID in cookie
    const activeCharacterId = getActiveCharacterId();
    if (activeCharacterId) {
      console.log('🍪 Persistence: Found active character ID in cookie:', activeCharacterId);
      // Try to load the character by ID
      localCharacterService
        .loadCharacter(activeCharacterId)
        .then((character) => {
          if (character) {
            console.log('📂 Persistence: Loaded character from cookie ID');
            load(character);
          } else {
            console.log(
              '📭 Persistence: Character from cookie ID not found, falling back to localStorage'
            );
            // Fallback to localStorage if cookie character not found
            if (hasStored()) {
              loadFromLocalStorage();
            }
          }
        })
        .catch((error) => {
          console.error('❌ Persistence: Failed to load character from cookie ID:', error);
          // Fallback to localStorage on error
          if (hasStored()) {
            loadFromLocalStorage();
          }
        });
    } else if (hasStored()) {
      console.log('📂 Persistence: Found stored character in localStorage, loading...');
      loadFromLocalStorage();
    } else {
      console.log('📭 Persistence: No stored character found');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  return {
    isLoading,
    hasUnsavedChanges,
    lastSaved,
    validationErrors,
    hasPersistenceErrors,
    saveNow,
    loadFromLocalStorage,
    hasStored,
    clearStored
  };
}
